import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { EVALUATION_LABELS, EVALUATION_STATUSES, type EvaluationStatus } from '@wasity/shared';
import { Card, EmptyState, ErrorNotice, Input, PageHeader, Pagination, Select, Skeleton, Table, Toolbar } from '@/components/ui';
import { useI18n, formatRelative } from '@/i18n';
import { api, qs } from '@/lib/api';
import { money } from '@/lib/format';
import type { EV, UserRow } from '@/lib/types';
import { AdminEvChip } from './common';

const LIMIT = 50;

/**
 * The search box, held back a moment before it becomes a request.
 *
 * The search is the server's now — filtering the page already in the browser told an admin that a
 * record on the next page did not exist — so every keystroke would otherwise be a round trip.
 */
function useDebounced(value: string, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export function AdminEvaluations() {
  const { t, locale, pick, localTitle } = useI18n();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | EvaluationStatus>('');
  const [evaluatorId, setEvaluatorId] = useState('');
  const [offset, setOffset] = useState(0);
  const search = useDebounced(q);
  // A new search starts at the first page. Without this, typing while on page 3 searched from row 100.
  useEffect(() => setOffset(0), [search]);
  const list = useQuery({ queryKey: ['admin', 'ev', 'list', status, evaluatorId, search, offset], queryFn: () => api.get<{ items: EV[] }>(`/api/admin/evaluations${qs({ status, evaluatorUserId: evaluatorId, q: search, limit: LIMIT, offset })}`) });
  const evaluators = useQuery({ queryKey: ['admin', 'users', 'evaluator', 'all'], queryFn: () => api.get<{ items: UserRow[] }>('/api/admin/users?role=evaluator&limit=200'), staleTime: 60_000 });
  const rows = list.data?.items ?? [];
  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: '/admin', label: t('nav.dashboard') }, { label: t('nav.evaluations') }]} title={t('nav.evaluations')} />
      <Toolbar end={list.data ? t('admin.requests.totalN', { n: rows.length }) : ''}>
        <Input className="w-[260px] max-w-full" placeholder={t('app.search')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('app.search')} />
        <Select className="w-[220px]" value={status} onChange={(e) => { setStatus(e.target.value as '' | EvaluationStatus); setOffset(0); }} aria-label={t('app.status')}>
          <option value="">{t('admin.requests.allStatuses')}</option>
          {EVALUATION_STATUSES.map((s) => <option key={s} value={s}>{pick(EVALUATION_LABELS[s])}</option>)}
        </Select>
        <Select className="w-[200px]" value={evaluatorId} onChange={(e) => { setEvaluatorId(e.target.value); setOffset(0); }} aria-label={t('app.evaluator')}>
          <option value="">{t('app.all')}</option>
          {evaluators.data?.items.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </Select>
      </Toolbar>
      {list.isLoading ? <Skeleton lines={8} /> : list.error ? <ErrorNotice error={list.error} retry={() => void list.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} /> : rows.length ? (
        <Card className="p-0 overflow-hidden">
          <Table className="border-0 rounded-none">
            <thead><tr><th>{t('app.number')}</th><th>{t('app.property')}</th><th>{t('app.owner')}</th><th>{t('app.evaluator')}</th><th>{t('app.fee')}</th><th>{t('app.status')}</th><th>{t('app.updated')}</th></tr></thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td className="num"><Link to={`/admin/evaluations/${e.id}`}>{e.number}</Link></td>
                  <td>{localTitle(e)} · <span className="num">{e.areaM2} {t('app.m2')}</span></td>
                  <td>{e.ownerName ?? '—'}</td>
                  <td className={e.evaluatorName ? '' : 'text-muted'}>{e.evaluatorName ?? '—'}</td>
                  <td className={e.totalHalalas ? 'num' : 'text-muted'}>{e.totalHalalas ? money(e.totalHalalas, locale) : '—'}</td>
                  <td><AdminEvChip ev={e} /></td>
                  <td className="text-muted text-[13px] whitespace-nowrap">{formatRelative(e.updatedAt, locale)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : <EmptyState title={t('app.noResults')} />}
      <Pagination offset={offset} limit={LIMIT} count={list.data?.items.length ?? 0} onChange={setOffset} labels={{ prev: t('app.prev'), next: t('app.next'), page: t('app.page') }} />
    </div>
  );
}
